import { updateField } from 'vuex-map-fields';
import { baseFilterFieldModel, generateFilterObject } from '../filter-field-model';

export const mutations = {
  updateField,

  /**
   * Mutation to set distinct fields for filter pane.
   */
  setDistinctFields(state, distinctFields) {
    state.distinctFields = distinctFields;
    // Creating model object for field to bind its selected value
    let filterValueModel = distinctFields.reduce(
      (selectionObject, field) => ({
        [field.Field]: baseFilterFieldModel(field.Type),
        ...selectionObject
      }),
      {}
    );
    state.filterValueModel = { ...filterValueModel, ...state.filterValueModel };
  },

  /**
   * Sets cockpit's active dashboard's brRefs by cockpit uuid
   */
  setBrRefsByCockpitUUID(state, { cockpitUUID, brRefs }) {
    state.brRefsByCockpitUUID[cockpitUUID] = brRefs;
  },

  /**
   * Sets dashboard br refs (parent dashboard) called from the Pages (design and dynamic display)
   */
  setDashboardBrRefs(state, brRefs) {
    state.dashboardBrRefs = brRefs;
  },

  setBrRefsByObjectUiUUID(state, { objectTemplateUUID, brRefs }) {
    state.brRefsByObjectTemplateUUID[objectTemplateUUID] = brRefs;
  },

  setDistinctFieldsCache(state, { brRefs, result }) {
    state.distinctFieldsCache[brRefs] = result;
  },

  /**
   * Sets page ref in the state
   * @param {Object} state state object
   * @param {String} pageRef page ref
   */
  setPageRef(state, pageRef) {
    state.pageRef = pageRef;
  },

  /**
   * Sets filterValueModel in the state. It is to be called when we are refresing the page and need to set the filter again.
   * @param {Object} state state object
   * @param {String} filterValueModel Object with the selected fields
   */
  setFilterValueModel(state, filterValueModel) {
    state.filterValueModel = filterValueModel;
  },

  /**
   * Adds appliedPageFilters for the column
   * @param {Object} state state object
   * @param {String} param1 column name of which applied filter needs to be updated
   * @param {Object} param2 new selection object
   */
  addAppliedPageFilter(state, { columnName, selectionObject }) {
    let updatedAppliedPageFilters = state.appliedPageFilters.concat([
      generateFilterObject(columnName, selectionObject, state.distinctFields)
    ]);
    state.appliedPageFilters = updatedAppliedPageFilters;
  },

  /**
   * Updates appliedPageFilters for the column
   * @param {Object} state state object
   * @param {String} param1 column name of which applied filter needs to be updated
   * @param {Object} param2 new selection object
   */
  updateExistingAppliedPageFilter(state, { columnName, selectionObject }) {
    let updatedAppliedPageFilters = state.appliedPageFilters.map((appliedPageFilter) => {
      if (appliedPageFilter.name === columnName) {
        return generateFilterObject(appliedPageFilter.name, selectionObject, state.distinctFields);
      }
      return appliedPageFilter;
    });
    state.appliedPageFilters = updatedAppliedPageFilters;
  },

  /**
   * removed applied page filters of specified columns
   * @param {Object} state state object
   * @param {String[]} columns columns of which applied page filters are to be removed
   */
  removeAppliedPageFiltersForColumns(state, columns) {
    state.appliedPageFilters = state.appliedPageFilters.filter(
      (appliedFilter) => !columns.includes(appliedFilter.name)
    );
  },

  /**
   * Resets filter model for given columns (fields)
   * @param {Object} state state object
   * @param {String[]} columns Columns for which filter model needs to be reset
   */
  resetFilterModelForColumns(state, columns) {
    const updatedModel = columns.reduce(
      (newModel, field) => ({
        ...newModel,
        [field]: baseFilterFieldModel()
      }),
      {}
    );
    state.filterValueModel = { ...state.filterValueModel, ...updatedModel };
  },

  /**
   * Updates filter model with given new model
   * @param {Object} state state object
   * @param {Object} newModel updated filter model
   */
  updateFilterValueModel(state, newModel) {
    state.filterValueModel = { ...state.filterValueModel, ...newModel };
  },

  /**
   * Add filter model for given columns (fields). This is basically for the filter bar created section.
   * @param {Object} state state object
   * @param {String[]} columns Columns for which filter model needs to be added
   */
  addFilterModelForColumns(state, columns) {
    const updatedModel = columns.reduce(
      (newModel, field) => ({
        ...newModel,
        [field]: baseFilterFieldModel()
      }),
      {}
    );
    state.filterValueModel = { ...updatedModel, ...state.filterValueModel };
  },

  /**
   * Mutation to set the preload filters in the state
   * @param {Object} state state object
   * @param {Object[]} preloadFilters selected fields for preload filters
   */
  setPreloadFilters(state, preloadFilters) {
    state.preloadFilters = preloadFilters || [];
  },

  /**
   * Mutation to reset the preload filters in the state
   * @param {Object} state
   */
  resetPreloadFilters(state) {
    state.preloadFilters = [];
  },

  /**
   * Sets active chart for multi dimension filters
   * @param {Object} state state
   * @param {String} chartUUID uuid of chart
   */
  setActiveChartForMultiDimFilters(state, chartUUID) {
    state.activeChartForMultiDimFilters = chartUUID;
  },

  /**
   * Sets selected dim filters for chart
   * @param {Object} state state
   * @param {Object[]} filters filters selected from the chart
   */
  setSelectedChartDimFilters(state, filters) {
    state.selectedChartDimFilters = filters;
  },

  /**
   * Method to reset the applied page filters
   * @param {Object} state state
   */
  resetAppliedFilters(state) {
    state.appliedPageFilters = [];
  },

  /**
   * Updates access-control widget filter model with given new model
   * @param {Object} state state object
   * @param {Object} acFilterObject ac-filter model
   */
  updateAcWidgetFilters(state, acFilterObject) {
    state.acWidgetFilters = { ...state.acWidgetFilters, ...acFilterObject };
  },

  /**
   * Method to reset the access cockpit widget filters
   * @param {Object} state state
   */
  resetACWidgetFilters(state) {
    state.acWidgetFilters = {};
  },

  /**
   * Removes specified access-control widget filters
   * @param {Object} state state object
   * @param {String[]} filters filters which are to be removed from ac widget filters
   */
  removeSpecificAcWidgetFilters(state, filters) {
    const newFilters = {};
    Object.keys(state.acWidgetFilters)
      .filter((key) => !filters.includes(key))
      .forEach((key) => {
        newFilters[key] = state.acWidgetFilters[key];
      });
    state.acWidgetFilters = newFilters;
  },

  /**
   * Method to set the filters in the undo array
   * @param {*} state state object
   * @param {*} record hold the object that have operation value and the filter object
   */
  setUndoFilters(state, record) {
    state.undoFilters = state.undoFilters.filter((obj) => obj.filter.name !== record.filter.name);
    state.undoFilters.push(record);
  },

  /**
   * Method to set the value in the redo filters
   * @param {*} state state object
   * @param {*} filter hold the object that have operation value and the filter object
   */
  setRedoFilters(state, filter) {
    state.redoFilters.push(filter);
  },

  /**
   * Method to simply add the filter to available page filters
   * @param {*} state state object
   * @param {*} filter field detail that to be applied as filter
   */
  pushToAppliedPageFilters(state, filter) {
    state.appliedPageFilters.push(filter);
  },

  /**
   * Method to reset the undo filters
   * @param {*} state state object
   */
  resetUndoFilters(state) {
    state.undoFilters = [];
  },

  /**
   * Method to reset the redo filter array
   * @param {*} state state object
   */
  resetRedoFilters(state) {
    state.redoFilters = [];
  },

  /**
   * To set loading flag
   * */
  setLoadingDistinctFields(state, value) {
    state.loadingDistinctFields = value;
  },

  /**
   * To set distinct fields loaded flag
   * */
  setHasLoadedDistinctFields(state, value) {
    state.hasLoadedDistinctFields = value;
  }
};
